import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useToast } from "./common/Toast";
import { useTx } from "../context/TxContext";
import { useApprove } from "../hooks/useApprove";
import { getPurchaseContract } from "../web3/contracts";
import { parseError } from "../utils/errors";
import { parseUsdt } from "../utils/format";
import { PACKAGES } from "../utils/constants";
import PackageCard from "./PackageCard";
import "./Packages.css";

// `packages` is the bool[9] from getUserPackages; `packageDetails` is the
// array of getUserPackageDetails tuples (index 0 = package 1).
export default function Packages({ packages, packageDetails, usdtBal, onDone }) {
  const { signer } = useWeb3();
  const toast = useToast();
  const { run } = useTx();
  const { ensureAllowance } = useApprove();

  const active = packages ? packages.map((b) => !!b) : new Array(9).fill(false);

  const statusFor = (pkg) => {
    const idx = pkg.id - 1;
    if (active[idx]) return "active";
    // upgradable if the immediately-previous package is active
    if (idx === 0 || active[idx - 1]) return "upgrade";
    return "locked";
  };

  const ensureFunds = (pkg) => {
    if (usdtBal && usdtBal.lt(parseUsdt(pkg.price))) {
      toast.error(`Not enough USDT for $${pkg.price}.`);
      return false;
    }
    return true;
  };

  const onUpgrade = async (pkg) => {
    if (!signer || !ensureFunds(pkg)) return;
    try {
      await run("Approving USDT", async () => {
        const approved = await ensureAllowance(parseUsdt(pkg.price));
        if (approved) toast.success("USDT approved.");
      });
      await run(`Upgrading to ${pkg.label}`, async () => {
        const purchase = getPurchaseContract(signer);
        const tx = await purchase.upgradePackage(ethers.BigNumber.from(pkg.id));
        await tx.wait();
      });
      toast.success(`${pkg.label} activated!`);
      onDone && onDone();
    } catch (e) {
      toast.error(parseError(e));
    }
  };

  const onRetopup = async (pkg) => {
    if (!signer || !ensureFunds(pkg)) return;
    try {
      await run("Approving USDT", async () => {
        const approved = await ensureAllowance(parseUsdt(pkg.price));
        if (approved) toast.success("USDT approved.");
      });
      await run(`Re-topping up ${pkg.label}`, async () => {
        const purchase = getPurchaseContract(signer);
        const tx = await purchase.retopupPackage(ethers.BigNumber.from(pkg.id));
        await tx.wait();
      });
      toast.success(`${pkg.label} re-topped up. Ceiling extended.`);
      onDone && onDone();
    } catch (e) {
      toast.error(parseError(e));
    }
  };

  return (
    <div className="pkg-list stagger">
      {PACKAGES.map((pkg) => (
        <PackageCard
          key={pkg.id}
          pkg={pkg}
          status={statusFor(pkg)}
          detail={packageDetails ? packageDetails[pkg.id - 1] : null}
          onUpgrade={onUpgrade}
          onRetopup={onRetopup}
        />
      ))}
    </div>
  );
}
